import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { VariationDirection, VariationDimension } from '@/types'

export interface DirectionCardProps {
  direction: VariationDirection
  isSelected: boolean
  onSelect: () => void
  disabled?: boolean
  multiSelect?: boolean
}

const dimensionLabels: Record<VariationDimension, string> = {
  style: 'Style',
  scene: 'Scene',
  composition: 'Composition',
  mood: 'Mood',
  color_lighting: 'Color/Light',
  target_audience: 'Audience',
}

const dimensionColors: Record<VariationDimension, string> = {
  style: 'bg-purple-100 text-purple-700',
  scene: 'bg-green-100 text-green-700',
  composition: 'bg-blue-100 text-blue-700',
  mood: 'bg-pink-100 text-pink-700',
  color_lighting: 'bg-amber-100 text-amber-700',
  target_audience: 'bg-cyan-100 text-cyan-700',
}

export function DirectionCard({
  direction,
  isSelected,
  onSelect,
  disabled = false,
  multiSelect = false,
}: DirectionCardProps) {
  const dimensionLabel = dimensionLabels[direction.dimension] || direction.dimension
  const dimensionColor = dimensionColors[direction.dimension] || 'bg-gray-100 text-gray-700'

  return (
    <div
      onClick={!disabled ? onSelect : undefined}
      className={cn(
        'p-4 border rounded-xl cursor-pointer transition-all duration-200 relative',
        isSelected && 'border-blue-500 bg-blue-50 ring-2 ring-blue-200',
        !isSelected && !disabled && 'hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {/* Multi-select checkbox indicator */}
      {multiSelect && (
        <div
          className={cn(
            'absolute top-2 right-2 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
            isSelected
              ? 'bg-blue-500 border-blue-500'
              : 'bg-white border-gray-300'
          )}
        >
          {isSelected && <Check className="h-3 w-3 text-white" />}
        </div>
      )}
      <div className="flex items-center gap-2 mb-2">
        <span className={cn('text-xs font-medium px-2 py-0.5 rounded', dimensionColor)}>
          {dimensionLabel}
        </span>
      </div>
      <h3 className="font-semibold text-gray-900">{direction.name}</h3>
      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{direction.description}</p>
    </div>
  )
}
